import { combineReducers } from 'redux';
import _union from 'lodash/union';
import { getExpirationFromToken, setTokenCookie } from '../../utilities';
import { normalizeAPIError } from '../../errors';

const setUserValue = (state, id, key, value) => {
  return {
    ...state,
    items: {
      ...state.items,
      [id]: {
        ...state.items[id],
        [key]: value,
      },
    },
  };
};

const removeUsersByEmail = (state, email) => {
  const newItems = { ...state.items };
  Object.keys(newItems).forEach(id => {
    if (newItems[id].auth_info.username === email) {
      delete newItems[id];
    }
  });
  return {
    ...state,
    items: newItems,
  };
};

const byId = (
  state = {
    isFetching: false,
    error: null,
    items: {},
  },
  action
) => {
  switch (action.type) {
    case 'FETCH_USER_REQUEST':
      return {
        ...state,
        error: null,
        isFetching: true,
      };
    case 'FETCH_USER_SUCCESS':
    case 'FETCH_STUDENTS_IN_CLASS_SUCCESS':
    case 'ADD_STUDENT_TO_CLASS':
    case 'ADD_USER_TO_USER_GROUP':
    case 'FETCH_USER_GROUP_SUCCESS':
    case 'INVITED_TEACHER_SIGN_UP_SUCCESS':
    case 'RESET_PASSWORD_SUCCESS':
      // Re-invited teachers currently get a new ID, so
      // the action passes a flag if it is a dupe.
      if (action.isDuplicate) return state;
      return {
        ...state,
        error: null,
        isFetching: false,
        items: {
          ...state.items,
          ...action.response.entities.users,
        },
      };
    case 'FETCH_USER_FAILURE':
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    case 'FETCH_STUDENTS_IN_CLASS_FAILURE':
      return {
        ...state,
        isFetching: false,
      };
    case 'POST_LOGOUT_SUCCESS':
      return {
        ...state,
        error: null,
        items: {},
      };
    case 'DEACTIVATE_USER_REQUEST':
      return setUserValue(state, action.id, 'isDeactivating', true);
    case 'DEACTIVATE_USER_FAILURE':
      return setUserValue(state, action.id, 'isDeactivating', false);
    case 'DEACTIVATE_USER_SUCCESS': {
      const deactivatedState = setUserValue(
        state,
        action.id,
        'isDeactivating',
        false
      );
      return setUserValue(deactivatedState, action.id, 'isDeactivated', true);
    }
    case 'RESEND_TEACHER_INVITE_REQUEST':
      return setUserValue(state, action.id, 'resendingInvite', true);
    case 'RESEND_TEACHER_INVITE_SUCCESS':
    case 'RESEND_TEACHER_INVITE_FAILURE':
      return setUserValue(state, action.id, 'resendingInvite', false);
    case 'REMOVE_TEACHER_INVITE_REQUEST':
      return setUserValue(state, action.id, 'requestingRemoval', true);
    case 'REMOVE_TEACHER_INVITE_SUCCESS':
      return removeUsersByEmail(state, action.email);
    case 'REMOVE_TEACHER_INVITE_FAILURE':
      return setUserValue(state, action.id, 'requestingRemoval', false);
    default:
      return state;
  }
};

const initialInvitedTeachersState = {
  isPosting: false,
  emailsPosting: [],
  teachersInvited: 0,
  resultsByEmail: {},
};

export const invitedTeachers = (
  state = initialInvitedTeachersState,
  action
) => {
  const { type, email, originalEmail } = action;
  const emailId = originalEmail || email;
  switch (type) {
    case 'POST_INVITE_TEACHER_REQUEST':
      return {
        ...state,
        isPosting: true,
        teachersInvited: !originalEmail
          ? state.teachersInvited + 1
          : state.teachersInvited,
        emailsPosting: [...state.emailsPosting, email],
      };
    case 'POST_INVITE_TEACHER_SUCCESS':
    case 'POST_INVITE_TEACHER_FAILURE': {
      const emailsPosting = state.emailsPosting.filter(e => e !== emailId);
      const resultsByEmail = {
        ...state.resultsByEmail,
        [email]: {
          type: action.error ? 'error' : 'success',
          response: action.error || action.response,
        },
      };
      if (originalEmail) delete resultsByEmail[originalEmail];
      return {
        ...state,
        emailsPosting,
        isPosting: emailsPosting.length > 0,
        resultsByEmail,
      };
    }
    case 'CLEAR_AN_INVITED_TEACHER': {
      const clearedResultsByEmail = { ...state.resultsByEmail };
      delete clearedResultsByEmail[originalEmail];
      return {
        ...state,
        teachersInvited: state.teachersInvited - 1,
        resultsByEmail: clearedResultsByEmail,
      };
    }
    case 'CLEAR_INVITED_TEACHERS':
      return initialInvitedTeachersState;
    default:
      return state;
  }
};

export const activeUser = (
  state = {
    isFetching: true,
    user: null,
  },
  action
) => {
  switch (action.type) {
    case 'CHECK_COOKIE_FOR_AUTH_TOKEN':
    case 'SET_AUTH_TOKEN':
      return {
        ...state,
        isFetching: action.token !== null,
      };
    case 'FETCH_ACTIVE_USER_REQUEST':
      return {
        ...state,
        isFetching: true,
      };
    case 'FETCH_ACTIVE_USER_SUCCESS':
    case 'INVITED_TEACHER_SIGN_UP_SUCCESS':
    case 'RESET_PASSWORD_SUCCESS': {
      const user = action.response.entities.users[action.response.result[0]];
      if (!Object.keys(user).includes('hints-seen')) {
        user['hints-seen'] = [];
      }
      return {
        isFetching: false,
        user,
      };
    }
    case 'FETCH_ACTIVE_USER_FAILURE':
    case 'POST_LOGOUT_SUCCESS':
      return {
        isFetching: false,
        user: null,
      };
    case 'POST_UI_HINT_SUCCESS': {
      const newHints = _union(state.user['hints-seen'], [action.hintKey]);
      return {
        ...state,
        user: {
          ...state.user,
          'hints-seen': newHints,
        },
      };
    }
    case 'USER_ACCOUNT_SETTINGS_UPDATED': {
      if (state.user.id !== action.id) return state;
      const newUser = { ...state.user };
      Object.keys(action.fields).forEach(key => {
        const { value } = action.fields[key];
        if (key === 'username') {
          newUser.auth_info.username = value;
        } else {
          newUser[key] = value;
        }
      });
      return {
        ...state,
        user: newUser,
      };
    }
    default:
      return state;
  }
};

export const auth = (
  state = {
    isPosting: false,
    token: null,
    sensitiveTokenExpiration: null,
    tokenExpiration: null,
    schoolId: null,
    validatedSchoolCode: null,
    checkingToken: true,
    failedLoginErrorVars: {},
    message: null,
  },
  action
) => {
  const { token } = action;
  switch (action.type) {
    case 'CHECK_COOKIE_FOR_AUTH_TOKEN':
    case 'SET_AUTH_TOKEN':
      return {
        ...state,
        checkingToken: false,
        token,
        sensitiveTokenExpiration: getExpirationFromToken(token, 'sxp'),
        tokenExpiration: getExpirationFromToken(token),
      };
    case 'CHECK_COOKIE_FOR_ACTIVE_SCHOOL':
      return {
        ...state,
        schoolId: action.schoolId ? parseInt(action.schoolId, 10) : null,
      };
    case 'INVITED_TEACHER_SIGN_UP_SUCCESS':
    case 'RESET_PASSWORD_SUCCESS': {
      const user = action.response.entities.users[action.response.result[0]];
      const invitedTeacherToken = user.auth_info.token;
      setTokenCookie(invitedTeacherToken);
      return {
        ...state,
        isPosting: false,
        token: invitedTeacherToken,
        sensitiveTokenExpiration: getExpirationFromToken(
          invitedTeacherToken,
          'sxp'
        ),
        tokenExpiration: getExpirationFromToken(invitedTeacherToken),
        schoolId: user.roles[0][0],
      };
    }
    case 'UPDATE_AUTH_TOKEN':
    case 'POST_LOGIN_SUCCESS':
    case 'POST_FREE_TRIAL_SIGNUP_SUCCESS':
      return {
        ...state,
        isPosting: false,
        validatedSchoolCode: null,
        token,
        sensitiveTokenExpiration: getExpirationFromToken(token, 'sxp'),
        tokenExpiration: getExpirationFromToken(token),
        failedLoginErrorVars: {},
      };
    case 'FAILED_LOGIN_IN_EXPIRED_SCHOOL':
      return {
        ...state,
        failedLoginErrorVars: action.failedLoginErrorVars,
      };
    case 'FETCH_ACTIVE_USER_SUCCESS': {
      const newActiveUser =
        action.response.entities.users[action.response.result[0]];
      const activeSchoolId = state.schoolId
        ? state.schoolId
        : newActiveUser.roles[0][0];
      return {
        ...state,
        schoolId: activeSchoolId,
      };
    }
    case 'FETCH_ACTIVE_USER_FAILURE':
      return {
        ...state,
        token: null,
        sensitiveTokenExpiration: null,
        tokenExpiration: null,
        schoolId: null,
      };
    case 'CLEAR_AUTH_MESSAGE':
      return {
        ...state,
        message: null,
      };
    case 'CHANGE_ACTIVE_SCHOOL':
      return {
        ...state,
        schoolId: action.schoolId,
      };
    case 'POST_LOGOUT_SUCCESS':
      return {
        ...state,
        token: null,
        sensitiveTokenExpiration: null,
        tokenExpiration: null,
        schoolId: null,
        failedLoginErrorVars: {},
        message:
          typeof action.message !== 'undefined'
            ? action.message
            : {
                type: 'success',
                msgId: 'logout.success_message',
              },
      };
    case 'SCHOOL_CODE_IS_VALID':
      return {
        ...state,
        validatedSchoolCode: action.schoolCode,
      };
    case 'POST_LOGIN_REQUEST':
      return {
        ...state,
        isPosting: true,
      };
    case 'POST_LOGIN_FAILURE':
      return {
        ...state,
        isPosting: false,
      };
    default:
      return state;
  }
};

// This is used whenever we need to generate a temporary token
// Examples: reset password, invite user
const tokenReducerGenerator = (actionSlug, badTokenCodes) => {
  return (
    state = {
      token: null,
      isValidatingToken: false,
      isValid: undefined,
      user: null,
      message: null,
    },
    action
  ) => {
    const { type, token, response, error } = action;
    switch (type) {
      case `CHECK_${actionSlug}_TOKEN_REQUEST`:
        return {
          ...state,
          token,
          isValidatingToken: true,
        };
      case `CHECK_${actionSlug}_TOKEN_SUCCESS`:
        return {
          ...state,
          isValidatingToken: false,
          isValid: true,
          user: response.results,
        };
      case `CHECK_${actionSlug}_TOKEN_FAILURE`: {
        const isValid = badTokenCodes.includes(error.errorCode)
          ? false
          : undefined;
        const message = {
          ...normalizeAPIError(error, type),
          errorCode: error.errorCode,
        };
        return {
          ...state,
          token: null,
          isValid,
          isValidatingToken: false,
          message,
        };
      }
      default:
        return state;
    }
  };
};

const users = combineReducers({
  byId,
  invitedTeachers,
  activeUser,
  auth,
  invites: tokenReducerGenerator('INVITE', [33, 34, 35, 45]),
  resetPassword: tokenReducerGenerator('RESET_PASSWORD', [33, 34, 35, 45]),
});

export default users;
export const getUser = (state, id) => state.byId.items[id];
export const getUserError = state => state.byId.error;
export const getActiveUser = state => state.activeUser.user;
export const getActiveSchoolId = state => state.auth.schoolId;
export const getIsFetchingActiveUser = state => state.activeUser.isFetching;
export const getIsPostingLogin = state => state.users.auth.isPosting;
export const getValidatedSchoolCode = state => state.auth.validatedSchoolCode;
export const getHasUIHintBeenSeen = (state, hint) => {
  const { user } = state.activeUser;
  if (!user) return undefined;
  if (!Object.keys(user).includes('hints-seen')) return undefined;
  return user['hints-seen'].includes(hint);
};
export const getAuthToken = state => state.auth.token;
const isTokenExpired = (state, key) => {
  const exp = state.auth[key];
  if (!exp) return null;
  return exp <= Date.now();
};
export const isAuthTokenExpired = state =>
  isTokenExpired(state, 'tokenExpiration');
export const isSensitiveTokenExpired = state =>
  isTokenExpired(state, 'sensitiveTokenExpiration');
export const getAuthMessage = state => state.auth.message;
export const isInviteTokenValid = state => state.invites.isValid;
export const isInviteTokenBeingValidated = state =>
  state.invites.isValidatingToken;
export const getInvitedUser = state => state.invites.user;
export const getInviteTokenMessage = state => state.invites.message;

export const isResetPasswordTokenValid = state => state.resetPassword.isValid;
export const isResetPasswordTokenBeingValidated = state =>
  state.resetPassword.isValidatingToken;
export const getResetPasswordUser = state => state.resetPassword.user;
export const getResetPasswordTokenMessage = state =>
  state.resetPassword.message;

export const getIsPostingInvitedTeachers = state =>
  state.invitedTeachers.isPosting;
export const getInvitedTeachersPosting = state =>
  state.invitedTeachers.emailsPosting;
export const getInvitedTeachersResults = state =>
  state.invitedTeachers.resultsByEmail;
export const getTeachersInvitedCount = state =>
  state.invitedTeachers.teachersInvited;
export const getInvitedEmailsByResultStatus = state => {
  const results = state.invitedTeachers.resultsByEmail;
  const output = {
    success: [],
    error: [],
  };
  Object.keys(results).map(email => {
    const status = results[email].type;
    return output[status].push(email);
  });
  return output;
};
